'use client';

import {MarqueeItemFragment, MarqueeSectionFieldsFragment} from '@generated/graphql';
import {FC} from 'react';
import {Button, Typography} from '@mui/material';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import {isRenderableLinkButton} from '@/lib/ensure-link-button';
import ConditionalLinkButton from '@/components/ui/buttons/ConditionalLinkButton';
import {
  MarqueeWrapper,
  MarqueeTrack,
  MarqueeItemWrapper,
  MainImageBox,
  ContentCard,
  ContentCardOverlay,
} from './MarqueeSection.styles';

type MarqueeSectionProps = {
    section: MarqueeSectionFieldsFragment;
};

export const MarqueeSection: FC<MarqueeSectionProps> = ({section}) => {
  const items = (section.itemsCollection?.items ?? []).filter((item): item is MarqueeItemFragment => item !== null);

  if (items.length === 0) return null;

  // Ensure enough items to fill the screen; each item pair is ~660px wide,
  // so we need at least ~4 sets for large screens, then double for seamless loop.
  const minRepeats = Math.max(2, Math.ceil(8 / items.length));
  const repeatCount = minRepeats % 2 === 0 ? minRepeats : minRepeats + 1;
  const repeatedItems = Array.from({length: repeatCount}, () => items).flat();

  return (
    <MarqueeWrapper>
      <MarqueeTrack>
        {repeatedItems.map((item, index) => (
          <MarqueeItem key={`${item.sys.id}-${index}`} item={item} />
        ))}
      </MarqueeTrack>
    </MarqueeWrapper>
  );
};

const MarqueeItem: FC<{item: MarqueeItemFragment}> = ({item}) => {
  return (
    <MarqueeItemWrapper>
      {item.mainImage?.url && (
        <MainImageBox>
          <ContentfulImage
            src={item.mainImage.url}
            alt={item.heading ?? ''}
            width={300}
            height={200}
            focalX={item.mainImageFocalX}
            focalY={item.mainImageFocalY}
          />
        </MainImageBox>
      )}
      <ContentCard>
        {item.contentCardImage?.url && (
          <ContentfulImage
            src={item.contentCardImage.url}
            alt=''
            fill
            focalX={item.contentImageFocalX}
            focalY={item.contentImageFocalY}
            style={{objectFit: 'cover'}}
          />
        )}
        <ContentCardOverlay sx={!isRenderableLinkButton(item.ctaButton) ? {justifyContent: 'flex-end'} : undefined}>
          <Typography variant='h4' component='p' color='text.primary' style={{marginTop: 16}}>
            {item.heading}
          </Typography>
          {isRenderableLinkButton(item.ctaButton) && (
            <ConditionalLinkButton
              button={item.ctaButton}
              ButtonComponent={Button}
              buttonType='marquee'
            />
          )}
        </ContentCardOverlay>
      </ContentCard>
    </MarqueeItemWrapper>
  );
};
